import { Button, CircularProgress, Paper, Typography,useTheme , Theme  } from '@mui/material';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import { Field, Form, Formik, FormikHelpers } from 'formik';

import { ChangeEvent, useRef, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Cookies from 'universal-cookie';
import schemas from '../../../data/schemas';

import { urlHelper } from '../../../helpers/urlHelper';
import { createBusiness, clearCreateBusinessError } from '../../../store/action_creators/business.actions';
import { InclusionLawEnum, Status } from '../../../store/config/enums';
import { RootState } from '../../../store/config/types';
import variables from '../../../styles/_variables.module.scss';
import { ClientSecret } from '../../ClientSecret';
import { CustomSnackbar } from '../../CustomSnackbar';
import { ImageUpload } from '../../ImageUpload';
import { BusinessConfirmationDialog } from '../../dialogs/BusinessConfirmationDialog';
import { FormSelectField } from '../FormSelectField';
import { FormTextField } from '../FormTextField';
import options from '../../../components/Link/inputData';
import { passwordGenerator } from '../../../helpers/PasswordGenerator';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    checkbox: {
      padding: '0.2rem',
      paddingLeft: 0,
    },
    title: {
      color: variables.slateBlue,
      fontSize: '1.0625rem',
      fontWeight: +variables.semiBoldTextWeight,
    },
    text: {
      color: variables.slateBlue,
    },
    imageInstructions: {
      color: variables.slateBlue,
      textAlign: 'center',
      width: '70%',
      fontSize: '0.75rem',
      marginTop: '0.5rem',
    },
  }),
);

interface Values {
  prettyName: string;
  urlName: string;
  legalId: string;
  description: string;
  vat: number;
  defaultInclusionLaw: InclusionLawEnum;
}

interface BusinessFormProps {
  handleSuccess?: () => void;
}

function generateSecretText() {
  return passwordGenerator(16);
}

function BusinessForm({ handleSuccess }: BusinessFormProps) {
  const classes = useStyles(useTheme());
  const dispatch = useDispatch<any>();
  const { auth, business } = useSelector((state: RootState) => state);
  const cookies = new Cookies();
  
  console.log('Auth state:', auth);
  console.log('Auth account:', auth.account);
  console.log('Auth account userId:', auth.account?.userId);
  console.log('Cookies token:', cookies.get('token'));
  
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [openConfirmationDialog, setOpenConfirmationDialog] = useState<boolean>(false);
  const [formValues, setFormValues] = useState<Values | null>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const [imageError, setImageError] = useState<string | null>(null);
  const [secret, setSecret] = useState<string>(generateSecretText());
  const [creatingBusiness, setCreatingBusiness] = useState<boolean>(false);
  const [hasRedirectedCreate, setHasRedirectedCreate] = useState<boolean>(false);
  const navigate = useNavigate();

  // Reset create business redirection flag when errorCode changes  
  useEffect(() => {
    if (business.createBusinessErrorCode !== 300) {
      setHasRedirectedCreate(false);
    }
  }, [business.createBusinessErrorCode]);

  // Redirect to register step 2 if errorCode is 300
  useEffect(() => {
    if (business.createBusinessErrorCode === 300 && !hasRedirectedCreate) {
      setHasRedirectedCreate(true);
      dispatch(clearCreateBusinessError()); // Clear first to prevent loops
      navigate('/register?step=2');
    }
  }, [business.createBusinessErrorCode, navigate, dispatch, hasRedirectedCreate]);

  const handlePrettyNameChange = (
    event: ChangeEvent<HTMLInputElement>,
    setFieldValue: FormikHelpers<Values>['setFieldValue'],
  ) => {
    const webAddressName = urlHelper.generateUrlBusinessName(event.target.value);
    setFieldValue('urlName', webAddressName);
  };

  const businessConfirmation = () => {
    console.log('Business confirmation triggered');
    setOpenConfirmationDialog(false);
    setCreatingBusiness(true);

    // Try to get userId from auth.account, or decode from token as fallback
    let userId = auth.account?.userId;
    const token = cookies.get('token');

    console.log('Available userId:', userId);
    console.log('Available token:', token);

    // If no userId but we have a token, try to decode JWT payload
    if (!userId && token) {
      try {
        const parts = token.split('.');
        if (parts.length >= 2) {
          const payload = JSON.parse(window.atob(parts[1]));
          console.log('Decoded token payload:', payload);
          // Common claim names: sub, userId, id, or nested user
          userId = payload.userId || payload.sub || (payload.user && payload.user.userId) || undefined;
        }
      } catch (err) {
        console.warn('Failed to decode token payload', err);
      }
    }

    console.log('Resolved userId after decode attempt:', userId);

    if (formValues && selectedImage && userId) {
      console.log('Dispatching createBusiness with:', formValues, selectedImage);
      dispatch(
        createBusiness(
          {
            prettyName: formValues.prettyName,
            urlName: formValues.urlName,
            legalId: formValues.legalId,
            userId: userId,
            softDescriptor: formValues.description,
            vatRate: formValues.vat,
            clientSecret: secret,
            defaultInclusionLaw: formValues.defaultInclusionLaw,
          },
          selectedImage!,
        ),
      );
    } else {
      console.log('Missing data for business creation:', {
        formValues: !!formValues,
        selectedImage: !!selectedImage,
        userId: userId,
        authAccount: auth.account,
        token: !!token,
      });
      setCreatingBusiness(false);
      // Show user-friendly message: require login/validation
      alert('No se pudo crear la empresa: falta información de usuario. Asegúrate de haber iniciado sesión o validado tu cuenta por email.');
    }
  };

  const handleFormSubmit = (values: Values) => {
     console.log('Creating business with values: ', values, selectedImage, auth.account?.userId);
 
    setFormValues(values);
    setOpenConfirmationDialog(true);
  };

  const closeSnack = () => {
    setCreatingBusiness(false);
  };

  return (
    <>
      <Formik
        initialValues={{
          prettyName: '',
          urlName: '',
          legalId: '',
          description: '',
          vat: 0,
          defaultInclusionLaw: InclusionLawEnum.NONE,
        }}
        validationSchema={schemas.BusinessSchema}
        onSubmit={handleFormSubmit}
        isInitialValid={false}
      >
        {({ values, setFieldValue }) => {
          const law17934Selected = values.defaultInclusionLaw === InclusionLawEnum.LAW17934;
          
          return (
            <Form className="form">
              <Paper elevation={2}>
                <Typography className={classes.title}>Tu empresa</Typography>
                <Field
                  max={30}
                  className="row-field"
                  name="prettyName"
                  component={FormTextField}
                  type="text"
                  placeholder="Nombre"
                  onChange={(event: ChangeEvent<HTMLInputElement>) =>
                    handlePrettyNameChange(event, setFieldValue)
                  }
                />
                <Field
                  className="row-field-negative-margin"
                  name="urlName"
                  component={FormTextField}
                  type="text"
                  placeholder="Nombre para la dirección web"
                  startAdornment={`${window.location.origin}/`}
                />
                <div className="row-field">
                  <Typography variant="body2" className={classes.text}>
                    Por ejemplo: mvd-donuts
                  </Typography>
                  <Typography variant="body2" className={classes.text}>
                    No incluir la palabra "empresa". Ingresar solo letras y guiones "-". El nombre debe tener
                    entre 3 y 30 caracteres.
                  </Typography>
                </div>
                <Field
                  className="row-field"
                  name="legalId"
                  component={FormTextField}
                  type="text"
                  placeholder="RUT"
                  max={12}
                />
                <Field
                  className="row-field"
                  name="vat"
                  component={FormSelectField}
                  options={law17934Selected ? options.vatRateFor17934 : options.vatRate}
                  label="IVA"
                />
                <Field
                  className="row-field"
                  name="defaultInclusionLaw"
                  component={FormSelectField}
                  options={options.inclusionLaw}
                  label="Opción por defecto de Ley de Inclusión Financiera"
                  onChange={(event: React.ChangeEvent<HTMLSelectElement>) => {
                    const selectedValue: number = Number(event.target.value);
                    setFieldValue('defaultInclusionLaw', selectedValue);
                    const newVatRateOptions =
                      selectedValue === InclusionLawEnum.LAW17934 ? options.vatRateFor17934 : options.vatRate;
                    const currentVatRate = values.vat;
                    const isCurrentOptionValid = newVatRateOptions.some(
                      (option) => option.id === currentVatRate,
                    );
                    const updatedVatRate = isCurrentOptionValid ? currentVatRate : newVatRateOptions[0].id;
                    setFieldValue('vat', updatedVatRate);
                  }}
                />
                <Field
                  className="row-field"
                  name="description"
                  component={FormTextField}
                  type="text"
                  placeholder="Razón social"
                />
                <ClientSecret secret={secret} setSecret={setSecret} creating />
                <ImageUpload
                  image={selectedImage}
                  setImage={setSelectedImage}
                  error={imageError}
                  setError={setImageError}
                   imageRef={imageRef as unknown as React.RefObject<HTMLImageElement>}
                />
              </Paper>
              <div className="form-row">
                <div className="button-row-field">
                  <Button
                    type="submit"
                    color="primary"
                    variant="contained"
                    disableElevation
                    disabled={business.creatingBusiness || !!imageError || !selectedImage}
                  >
                    {business.creatingBusiness ? (
                      <CircularProgress style={{ color: '#FFF' }} size={20} thickness={6} />
                    ) : (
                      'Confirmar'
                    )}
                  </Button>
                </div>
              </div>
            </Form>
          );
        }}
      </Formik>
      <BusinessConfirmationDialog
        open={openConfirmationDialog}
        setOpen={setOpenConfirmationDialog}
        message="¿Deseas crear tu cuenta con esta información?"
        action={businessConfirmation}
      />
      <CustomSnackbar
        open={creatingBusiness && business.createBusinessErrorMessage !== null}
        message={business.createBusinessErrorMessage || 'Ocurrió un error al crear tu empresa'}
        handleClose={closeSnack}
        type={Status.ERROR}
      />
    </>
  );
}

export default BusinessForm;
